import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  Table,
  DatePicker,
  Button,
  Flex,
  Typography,
  Input,
  Segmented,
  Empty,
  Spin,
  Dropdown,
  Divider,
  message,
  ConfigProvider,
} from "antd";
import {
  DownloadOutlined,
  SearchOutlined,
  CalendarOutlined,
  DownOutlined,
  RightOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getInvoices } from "../../../services/invoiceService";
import { getCompanySettings } from "../../../services/organizationService";
import {
  normalizeGstInvoice,
  calculateGstFilingMetrics,
  groupInvoicesByGstRate,
  groupInvoicesByHsn,
  exportGstr1CsvFile,
  exportInvoicesCsvFile,
  exportHsnSummaryCsvFile,
  formatGstDate,
  getIndianFinancialYearInfo,
  resolvePeriodDates,
  QUARTER_KEYS,
  HALF_YEAR_KEYS,
  QUARTER_SECTIONS,
  HALF_YEAR_SECTIONS,
} from "../../../utils/gstFilingUtils";

const { Text } = Typography;
const { RangePicker } = DatePicker;

function getPresetOptions() {
  const fyInfo = getIndianFinancialYearInfo();
  return [
    { key: "all", label: "All Dates" },
    { key: "this_month", label: "This Month" },
    { key: "last_month", label: "Last Month" },
    { key: "fy_ytd", label: fyInfo.fullLabel },
    { key: "fy_last", label: fyInfo.prevFullLabel },
  ];
}

function formatNumber(amt) {
  return Number(amt || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function renderPaymentStatusBadge(status) {
  const s = (status || "unpaid").toLowerCase();
  let color = "#ef4444";
  if (s === "paid") {
    color = "#16a34a";
  } else if (s === "partially paid") {
    color = "#d97706";
  }
  return (
    <span style={{ fontSize: "0.75rem", fontWeight: 600, color, textTransform: "capitalize" }}>
      {status || "unpaid"}
    </span>
  );
}

/**
 * Excel-Style Grid Columns for Invoices in Exact Order:
 * Payment mode | B2B / B2C | GST Number | Client | Invoice Number | Date |
 * Amount Received | Balance | Total Invoice Value | Taxable Value | IGST | CGST | SGST | Status | Service | HSN
 */
function buildExcelGridColumns() {
  return [
    {
      title: "Payment mode",
      dataIndex: "paymentMode",
      key: "paymentMode",
      width: 110,
      align: "center",
      render: (mode) => (
        <span
          style={{
            fontWeight: 700,
            fontSize: "0.85rem",
            fontFamily: "monospace",
            color: mode === "G" ? "#2563eb" : "#475569",
          }}
        >
          {mode || "N"}
        </span>
      ),
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 130,
      align: "center",
      render: (method) => {
        if (!method) return <span style={{ color: "#94a3b8" }}>—</span>;
        return (
          <span
            style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: 4,
              fontSize: "0.75rem",
              fontWeight: 600,
              background: "#f1f5f9",
              color: "#334155",
              border: "1px solid #e2e8f0",
              whiteSpace: "nowrap",
            }}
          >
            {method}
          </span>
        );
      },
    },
    {
      title: "B2B / B2C",
      dataIndex: "isB2B",
      key: "isB2B",
      width: 95,
      align: "center",
      render: (isB2B) => (
        <span
          style={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: isB2B ? "#2563eb" : "#64748b",
          }}
        >
          {isB2B ? "B2B" : "B2C"}
        </span>
      ),
    },
    {
      title: "GST Number",
      dataIndex: "customerGst",
      key: "customerGst",
      width: 160,
      render: (val) => (
        <span style={{ fontSize: "0.8rem", color: val ? "#0f172a" : "#94a3b8", fontFamily: "monospace" }}>
          {val || "—"}
        </span>
      ),
    },
    {
      title: "Client",
      dataIndex: "customerName",
      key: "customerName",
      width: 190,
      ellipsis: true,
      render: (val) => (
        <span title={val} style={{ fontWeight: 600, color: "#1e293b" }}>
          {val || "—"}
        </span>
      ),
    },
    {
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 140,
      render: (val) => (
        <span style={{ fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>
          {val}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 105,
      render: (val) => formatGstDate(val),
    },
    {
      title: "Amount Received",
      dataIndex: "amountReceived",
      key: "amountReceived",
      width: 135,
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(amt)}
        </span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      width: 120,
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: amt > 0 ? "#dc2626" : "#475569" }}>
          {formatNumber(amt)}
        </span>
      ),
    },
    {
      title: "Total Invoice Value",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 145,
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#0f172a" }}>
          {formatNumber(amt)}
        </span>
      ),
    },
    {
      title: "Taxable Value",
      dataIndex: "taxableAmount",
      key: "taxableAmount",
      width: 130,
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "#0f172a" }}>
          {formatNumber(amt)}
        </span>
      ),
    },
    {
      title: "IGST",
      dataIndex: "igst",
      key: "igst",
      width: 110,
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {amt > 0 ? formatNumber(amt) : "—"}
        </span>
      ),
    },
    {
      title: "CGST",
      dataIndex: "cgst",
      key: "cgst",
      width: 110,
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {amt > 0 ? formatNumber(amt) : "—"}
        </span>
      ),
    },
    {
      title: "SGST",
      dataIndex: "sgst",
      key: "sgst",
      width: 110,
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {amt > 0 ? formatNumber(amt) : "—"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 105,
      align: "center",
      render: renderPaymentStatusBadge,
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      width: 160,
      ellipsis: true,
      render: (val) => (
        <span title={val} style={{ fontSize: "0.8rem", color: "#334155" }}>
          {val || "—"}
        </span>
      ),
    },
    {
      title: "HSN",
      dataIndex: "sacHsn",
      key: "sacHsn",
      width: 110,
      render: (val) => (
        <span style={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#334155" }}>
          {val || "998313"}
        </span>
      ),
    },
  ];
}

/**
 * Excel-Style Grid Columns for Rate-wise Slab Summary
 */
function buildRateGridColumns() {
  return [
    {
      title: "Tax Rate",
      dataIndex: "rateLabel",
      key: "rateLabel",
      width: 120,
      render: (val) => <span style={{ fontWeight: 600 }}>{val}</span>,
    },
    {
      title: "Invoices",
      dataIndex: "invoiceCount",
      key: "invoiceCount",
      width: 100,
      align: "center",
      render: (count) => count,
    },
    {
      title: "Taxable Value (₹)",
      dataIndex: "taxableAmount",
      key: "taxableAmount",
      align: "right",
      render: (amt) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatNumber(amt)}</span>,
    },
    {
      title: "CGST (₹)",
      dataIndex: "cgst",
      key: "cgst",
      align: "right",
      render: (amt) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatNumber(amt)}</span>,
    },
    {
      title: "SGST (₹)",
      dataIndex: "sgst",
      key: "sgst",
      align: "right",
      render: (amt) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatNumber(amt)}</span>,
    },
    {
      title: "IGST (₹)",
      dataIndex: "igst",
      key: "igst",
      align: "right",
      render: (amt) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatNumber(amt)}</span>,
    },
    {
      title: "Total Tax (₹)",
      dataIndex: "totalTax",
      key: "totalTax",
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
          {formatNumber(amt)}
        </span>
      ),
    },
    {
      title: "Total Value (₹)",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
          ₹{formatNumber(amt)}
        </span>
      ),
    },
  ];
}

/**
 * Renders an Excel-style bottom summary row for the invoice table
 */
function renderInvoiceTableSummary(pageData, selectedRowKeys = []) {
  const selectedKeySet = new Set(selectedRowKeys);
  let sumReceived = 0;
  let sumBalance = 0;
  let sumTotal = 0;
  let sumTaxable = 0;
  let sumIgst = 0;
  let sumCgst = 0;
  let sumSgst = 0;
  let activeCount = 0;

  pageData.forEach((row) => {
    if (selectedKeySet.has(row._id)) {
      activeCount += 1;
      sumReceived += Number(row.amountReceived || 0);
      sumBalance += Number(row.balance || 0);
      sumTotal += Number(row.totalAmount || 0);
      sumTaxable += Number(row.taxableAmount || 0);
      sumIgst += Number(row.igst || 0);
      sumCgst += Number(row.cgst || 0);
      sumSgst += Number(row.sgst || 0);
    }
  });

  return (
    <Table.Summary fixed="bottom">
      <Table.Summary.Row
        style={{
          backgroundColor: "#fffdf5",
          fontWeight: 700,
          borderTop: "2px solid #cbd5e1",
          fontSize: "0.82rem",
        }}
      >
        <Table.Summary.Cell index={0} colSpan={8}>
          <span
            style={{
              display: "inline-block",
              backgroundColor: "#f1f5f9",
              border: "1px solid #cbd5e1",
              color: "#0f172a",
              padding: "2px 8px",
              borderRadius: 4,
              fontWeight: 800,
              fontSize: "0.78rem",
              letterSpacing: "0.02em",
            }}
          >
            TOTAL ({activeCount} active / {pageData.length} records)
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={8} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(sumReceived)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={9} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(sumBalance)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={10} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(sumTotal)}
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={11}
          align="right"
          style={{
            backgroundColor: "#fef3c7",
            boxShadow: "inset 0 0 0 1px #f59e0b",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              display: "inline-block",
              backgroundColor: "#fde68a",
              color: "#92400e",
              border: "1px solid #f59e0b",
              padding: "2px 8px",
              borderRadius: "4px",
              fontWeight: 800,
              fontSize: "0.85rem",
              fontVariantNumeric: "tabular-nums",
            }}
            title="Total Taxable Value (Key for GSTR-1)"
          >
            {formatNumber(sumTaxable)}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={12} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {sumIgst > 0 ? formatNumber(sumIgst) : "—"}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={13} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {sumCgst > 0 ? formatNumber(sumCgst) : "—"}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={14} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {sumSgst > 0 ? formatNumber(sumSgst) : "—"}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={15} colSpan={3} />
      </Table.Summary.Row>
    </Table.Summary>
  );
}

/**
 * Renders an Excel-style bottom summary row for the rate slab table
 */
function renderRateTableSummary(pageData) {
  let sumTaxable = 0;
  let sumCgst = 0;
  let sumSgst = 0;
  let sumIgst = 0;
  let sumTotalTax = 0;
  let sumTotal = 0;

  pageData.forEach((row) => {
    sumTaxable += Number(row.taxableAmount || 0);
    sumCgst += Number(row.cgst || 0);
    sumSgst += Number(row.sgst || 0);
    sumIgst += Number(row.igst || 0);
    sumTotalTax += Number(row.totalTax || 0);
    sumTotal += Number(row.totalAmount || 0);
  });

  return (
    <Table.Summary fixed="bottom">
      <Table.Summary.Row
        style={{
          backgroundColor: "#fffdf5",
          fontWeight: 700,
          borderTop: "2px solid #cbd5e1",
          fontSize: "0.82rem",
        }}
      >
        <Table.Summary.Cell index={0} colSpan={2}>
          <span
            style={{
              display: "inline-block",
              backgroundColor: "#f1f5f9",
              border: "1px solid #cbd5e1",
              color: "#0f172a",
              padding: "2px 8px",
              borderRadius: 4,
              fontWeight: 800,
              fontSize: "0.78rem",
              letterSpacing: "0.02em",
            }}
          >
            TOTAL
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={2}
          align="right"
          style={{
            backgroundColor: "#fef3c7",
            boxShadow: "inset 0 0 0 1px #f59e0b",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              display: "inline-block",
              backgroundColor: "#fde68a",
              color: "#92400e",
              border: "1px solid #f59e0b",
              padding: "2px 8px",
              borderRadius: "4px",
              fontWeight: 800,
              fontSize: "0.85rem",
              fontVariantNumeric: "tabular-nums",
            }}
            title="Total Taxable Value"
          >
            {formatNumber(sumTaxable)}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(sumCgst)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(sumSgst)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(sumIgst)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={6} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(sumTotalTax)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={7} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          ₹{formatNumber(sumTotal)}
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
}

function formatHsnCellAmount(amt) {
  if (!amt || Number(amt) <= 0) return "";
  const num = Number(amt);
  if (num % 1 === 0) {
    return num.toLocaleString("en-IN");
  }
  return formatNumber(num);
}

/**
 * Excel-Style Grid Columns for Table 12 HSN Summary (Matches Official GST & Accounting format)
 */
function buildHsnGridColumns() {
  const taxHeaderStyle = {
    background: "#dbeafe",
    color: "#1e3a8a",
    fontWeight: 700,
  };

  return [
    {
      title: "HSN",
      dataIndex: "hsnCode",
      key: "hsnCode",
      width: 140,
      render: (val) => (
        <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>
          {val || "998313"}
        </span>
      ),
    },
    {
      title: "Total HSN",
      dataIndex: "totalCount",
      key: "totalCount",
      width: 120,
      align: "right",
      render: (val) => (
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "#334155" }}>
          {val}
        </span>
      ),
    },
    {
      title: "Taxable Value",
      dataIndex: "taxableValue",
      key: "taxableValue",
      width: 160,
      align: "right",
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "#0f172a" }}>
          {formatHsnCellAmount(amt)}
        </span>
      ),
    },
    {
      title: "IGST",
      dataIndex: "igstAmount",
      key: "igstAmount",
      width: 140,
      align: "right",
      onHeaderCell: () => ({ style: taxHeaderStyle }),
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#1e293b" }}>
          {formatHsnCellAmount(amt)}
        </span>
      ),
    },
    {
      title: "CGST",
      dataIndex: "cgstAmount",
      key: "cgstAmount",
      width: 140,
      align: "right",
      onHeaderCell: () => ({ style: taxHeaderStyle }),
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#1e293b" }}>
          {formatHsnCellAmount(amt)}
        </span>
      ),
    },
    {
      title: "SGST",
      dataIndex: "sgstAmount",
      key: "sgstAmount",
      width: 140,
      align: "right",
      onHeaderCell: () => ({ style: taxHeaderStyle }),
      render: (amt) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#1e293b" }}>
          {formatHsnCellAmount(amt)}
        </span>
      ),
    },
  ];
}

/**
 * Renders an Excel-style bottom summary row for the HSN summary table
 */
function renderHsnTableSummary(pageData) {
  let sumCount = 0;
  let sumTaxable = 0;
  let sumIgst = 0;
  let sumCgst = 0;
  let sumSgst = 0;

  pageData.forEach((row) => {
    sumCount += Number(row.totalCount || 0);
    sumTaxable += Number(row.taxableValue || 0);
    sumIgst += Number(row.igstAmount || 0);
    sumCgst += Number(row.cgstAmount || 0);
    sumSgst += Number(row.sgstAmount || 0);
  });

  return (
    <Table.Summary fixed="bottom">
      <Table.Summary.Row
        style={{
          backgroundColor: "#fffdf5",
          fontWeight: 700,
          borderTop: "2px solid #cbd5e1",
          fontSize: "0.82rem",
        }}
      >
        <Table.Summary.Cell index={0}>
          <span
            style={{
              display: "inline-block",
              backgroundColor: "#f1f5f9",
              border: "1px solid #cbd5e1",
              color: "#0f172a",
              padding: "2px 8px",
              borderRadius: 4,
              fontWeight: 800,
              fontSize: "0.78rem",
              letterSpacing: "0.02em",
            }}
          >
            TOTAL ({pageData.length} HSN)
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {sumCount}
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={2}
          align="right"
          style={{
            backgroundColor: "#fef3c7",
            boxShadow: "inset 0 0 0 1px #f59e0b",
            verticalAlign: "middle",
          }}
        >
          <div
            style={{
              display: "inline-block",
              backgroundColor: "#fde68a",
              color: "#92400e",
              border: "1px solid #f59e0b",
              padding: "2px 8px",
              borderRadius: "4px",
              fontWeight: 800,
              fontSize: "0.85rem",
              fontVariantNumeric: "tabular-nums",
            }}
            title="Total Taxable Value"
          >
            {formatNumber(sumTaxable)}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {sumIgst > 0 ? formatNumber(sumIgst) : ""}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {sumCgst > 0 ? formatNumber(sumCgst) : ""}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} align="right" style={{ fontVariantNumeric: "tabular-nums" }}>
          {sumSgst > 0 ? formatNumber(sumSgst) : ""}
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
}

function renderSheetTable({
  loading,
  activeView,
  hsnColumns,
  hsnRows,
  rateColumns,
  rateRows,
  gridColumns,
  displayedRecords,
  rowSelection,
  selectedRowKeys,
}) {
  if (loading) {
    return (
      <div style={{ padding: "80px 0", textAlign: "center" }}>
        <Spin size="large" description="Loading sheet..." />
      </div>
    );
  }

  if (activeView === "hsn_summary") {
    return (
      <Table
        bordered
        size="small"
        columns={hsnColumns}
        dataSource={hsnRows}
        rowKey="hsnCode"
        pagination={false}
        scroll={{ x: 800, y: 440 }}
        locale={{ emptyText: <Empty description="No HSN summary records found" /> }}
        summary={renderHsnTableSummary}
        className="excel-style-table"
      />
    );
  }

  if (activeView === "rate_summary") {
    return (
      <Table
        bordered
        size="small"
        columns={rateColumns}
        dataSource={rateRows}
        rowKey="rateLabel"
        pagination={false}
        scroll={{ x: 800, y: 440 }}
        locale={{ emptyText: <Empty description="No tax records found" /> }}
        summary={renderRateTableSummary}
        className="excel-style-table"
      />
    );
  }

  const selectedKeySet = new Set(selectedRowKeys || []);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            rowSelectedBg: "#ffffff",
            rowSelectedHoverBg: "#f8fafc",
          },
          Checkbox: {
            borderRadiusSM: 2.5,
            controlInteractiveSize: 14.5,
            colorPrimary: "#2563eb",
            colorPrimaryHover: "#1d4ed8",
          },
        },
      }}
    >
      <Table
        bordered
        size="small"
        columns={gridColumns}
        dataSource={displayedRecords}
        rowKey="_id"
        pagination={false}
        scroll={{ x: 2080, y: 440 }}
        rowSelection={rowSelection}
        rowClassName={(record) =>
          selectedKeySet.has(record._id) ? "gst-row-selected" : "gst-row-unselected"
        }
        onRow={(record) => {
          const isSelected = selectedKeySet.has(record._id);
          return {
            className: isSelected ? "gst-row-selected" : "gst-row-unselected",
            style: {
              opacity: isSelected ? 1 : 0.45,
              transition: "opacity 0.2s ease, background-color 0.2s ease",
            },
          };
        }}
        locale={{ emptyText: <Empty description="No invoices found in this view" /> }}
        summary={(pageData) => renderInvoiceTableSummary(pageData, selectedRowKeys)}
        className="excel-style-table"
      />
    </ConfigProvider>
  );
}

/**
 * Renders the nested dropdown menu for GST period selection
 */
function QuarterFlyoutSubmenu({ selectedPeriod, onSelectPreset }) {
  return (
    <div
      style={{
        width: 230,
        maxHeight: 390,
        overflowY: "auto",
        padding: "8px 6px",
        borderLeft: "1px solid #f1f5f9",
        background: "#fafbfd",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <div
        style={{
          padding: "4px 8px 6px",
          borderBottom: "1px solid #e2e8f0",
          marginBottom: 4,
        }}
      >
        <Text strong style={{ fontSize: "12px", color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Quarterly Periods
        </Text>
      </div>

      {QUARTER_SECTIONS.map((section, sIdx) => (
        <div key={section.title} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sIdx > 0 && <Divider style={{ margin: "4px 0" }} />}
          <div
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              padding: "4px 8px 2px",
              letterSpacing: "0.03em",
            }}
          >
            {section.title}
          </div>
          {section.items.map((item) => {
            const isSelected = selectedPeriod === item.key;
            return (
              <button
                type="button"
                key={item.key}
                onClick={() => onSelectPreset(item.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px",
                  borderRadius: 5,
                  fontSize: "12.5px",
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? "#2563eb" : "#1e293b",
                  background: isSelected ? "#eff6ff" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                  fontFamily: "inherit",
                  transition: "all 0.15s ease",
                }}
              >
                <span>{item.label}</span>
                {isSelected && <CheckOutlined style={{ fontSize: 11, color: "#2563eb" }} />}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function HalfYearFlyoutSubmenu({ selectedPeriod, onSelectPreset }) {
  return (
    <div
      style={{
        width: 230,
        maxHeight: 390,
        overflowY: "auto",
        padding: "8px 6px",
        borderLeft: "1px solid #f1f5f9",
        background: "#fafbfd",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <div
        style={{
          padding: "4px 8px 6px",
          borderBottom: "1px solid #e2e8f0",
          marginBottom: 4,
        }}
      >
        <Text strong style={{ fontSize: "12px", color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Half-Yearly Periods
        </Text>
      </div>

      {HALF_YEAR_SECTIONS.map((section, sIdx) => (
        <div key={section.title} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sIdx > 0 && <Divider style={{ margin: "4px 0" }} />}
          <div
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              padding: "4px 8px 2px",
              letterSpacing: "0.03em",
            }}
          >
            {section.title}
          </div>
          {section.items.map((item) => {
            const isSelected = selectedPeriod === item.key;
            return (
              <button
                type="button"
                key={item.key}
                onClick={() => onSelectPreset(item.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px",
                  borderRadius: 5,
                  fontSize: "12.5px",
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? "#2563eb" : "#1e293b",
                  background: isSelected ? "#eff6ff" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                  fontFamily: "inherit",
                  transition: "all 0.15s ease",
                }}
              >
                <span>{item.label}</span>
                {isSelected && <CheckOutlined style={{ fontSize: 11, color: "#2563eb" }} />}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function CustomRangeFlyoutSubmenu({
  pendingRange,
  onPendingRangeChange,
  onClose,
  onApplyCustom,
}) {
  return (
    <div
      style={{
        width: 280,
        padding: "12px 12px",
        borderLeft: "1px solid #f1f5f9",
        background: "#fafbfd",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <Flex justify="space-between" align="center">
        <Text strong style={{ fontSize: "13px", color: "#1e293b" }}>
          Custom Range
        </Text>
        <Text type="secondary" style={{ fontSize: "11px" }}>
          Select Dates
        </Text>
      </Flex>

      <div style={{ display: "flex", gap: 6 }}>
        <Button
          size="small"
          style={{ fontSize: "11px", borderRadius: 4, flex: 1, padding: "0 4px", whiteSpace: "nowrap" }}
          onClick={() => onPendingRangeChange([dayjs().startOf("month"), dayjs()])}
        >
          This Month → Today
        </Button>
        <Button
          size="small"
          style={{ fontSize: "11px", borderRadius: 4, flex: 1, padding: "0 4px", whiteSpace: "nowrap" }}
          onClick={() => onPendingRangeChange([dayjs().subtract(30, "day"), dayjs()])}
        >
          Last 30 Days → Today
        </Button>
      </div>

      <div>
        <RangePicker
          style={{ width: "100%" }}
          value={pendingRange}
          onChange={(dates) => onPendingRangeChange(dates)}
          allowClear={false}
          size="middle"
          getPopupContainer={(trigger) => trigger?.closest?.(".ant-modal-content") || trigger?.parentElement || document.body}
        />
      </div>

      <Flex justify="flex-end" gap={8} style={{ marginTop: 4 }}>
        <Button size="small" onClick={onClose} style={{ borderRadius: 4 }}>
          Cancel
        </Button>
        <Button
          type="primary"
          size="small"
          disabled={!pendingRange || pendingRange?.length !== 2}
          onClick={() => onApplyCustom(pendingRange)}
          style={{ background: "#2563eb", borderColor: "#2563eb", borderRadius: 4 }}
        >
          Apply
        </Button>
      </Flex>
    </div>
  );
}

function FlyoutTriggerButton({
  flyoutKey,
  label,
  activeFlyout,
  isGroupSelected,
  onFlyoutChange,
}) {
  const isOpen = activeFlyout === flyoutKey;
  let bg = "transparent";
  if (isOpen) {
    bg = "#eff6ff";
  } else if (isGroupSelected) {
    bg = "#f8fafc";
  }

  return (
    <button
      type="button"
      onClick={() => onFlyoutChange(flyoutKey)}
      onMouseEnter={() => onFlyoutChange(flyoutKey)}
      onFocus={() => onFlyoutChange(flyoutKey)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        borderRadius: 6,
        fontSize: "13px",
        fontWeight: isOpen || isGroupSelected ? 600 : 400,
        color: isOpen || isGroupSelected ? "#2563eb" : "#1e293b",
        background: bg,
        border: "none",
        cursor: "pointer",
        width: "100%",
        textAlign: "left",
        fontFamily: "inherit",
        transition: "all 0.15s ease",
      }}
    >
      <span>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {isGroupSelected && <CheckOutlined style={{ fontSize: 11, color: "#2563eb" }} />}
        <RightOutlined style={{ fontSize: 11, color: isOpen ? "#2563eb" : "#94a3b8" }} />
      </div>
    </button>
  );
}

function GstPeriodDropdownMenu({
  presetOptions,
  selectedPeriod,
  customRange,
  onSelectPreset,
  onClose,
  onApplyCustom,
}) {
  const [activeFlyout, setActiveFlyout] = useState(null);
  const [pendingRange, setPendingRange] = useState(() => (
    customRange?.length === 2
      ? customRange
      : [dayjs().startOf("month"), dayjs()]
  ));

  const isQuarterSelected = QUARTER_KEYS.includes(selectedPeriod);
  const isHalfYearSelected = HALF_YEAR_KEYS.includes(selectedPeriod);
  const isCustomSelected = selectedPeriod === "custom";

  return (
    <div
      style={{
        display: "flex",
        background: "#ffffff",
        borderRadius: 8,
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.04)",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
      }}
    >
      {/* Presets & Triggers Column */}
      <div
        style={{
          width: 215,
          padding: "8px 6px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          background: "#ffffff",
        }}
      >
        {presetOptions.slice(0, 3).map((item) => {
          const isSelected = selectedPeriod === item.key;
          return (
            <button
              type="button"
              key={item.key}
              onClick={() => onSelectPreset(item.key)}
              onMouseEnter={() => setActiveFlyout(null)}
              onFocus={() => setActiveFlyout(null)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                borderRadius: 6,
                fontSize: "13px",
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? "#2563eb" : "#1e293b",
                background: isSelected ? "#eff6ff" : "transparent",
                border: "none",
                cursor: "pointer",
                width: "100%",
                textAlign: "left",
                fontFamily: "inherit",
                transition: "all 0.15s ease",
              }}
            >
              <span>{item.label}</span>
              {isSelected && <CheckOutlined style={{ fontSize: 12, color: "#2563eb" }} />}
            </button>
          );
        })}

        {/* Nested Trigger: Quarter */}
        <FlyoutTriggerButton
          flyoutKey="quarter"
          label="Quarter"
          activeFlyout={activeFlyout}
          isGroupSelected={isQuarterSelected}
          onFlyoutChange={setActiveFlyout}
        />

        {/* Nested Trigger: Half Year */}
        <FlyoutTriggerButton
          flyoutKey="half_year"
          label="Half Year"
          activeFlyout={activeFlyout}
          isGroupSelected={isHalfYearSelected}
          onFlyoutChange={setActiveFlyout}
        />

        {/* Financial Year Presets */}
        {presetOptions.slice(3).map((item) => {
          const isSelected = selectedPeriod === item.key;
          return (
            <button
              type="button"
              key={item.key}
              onClick={() => onSelectPreset(item.key)}
              onMouseEnter={() => setActiveFlyout(null)}
              onFocus={() => setActiveFlyout(null)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                borderRadius: 6,
                fontSize: "13px",
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? "#2563eb" : "#1e293b",
                background: isSelected ? "#eff6ff" : "transparent",
                border: "none",
                cursor: "pointer",
                width: "100%",
                textAlign: "left",
                fontFamily: "inherit",
                transition: "all 0.15s ease",
              }}
            >
              <span>{item.label}</span>
              {isSelected && <CheckOutlined style={{ fontSize: 12, color: "#2563eb" }} />}
            </button>
          );
        })}

        <Divider style={{ margin: "4px 0" }} />

        {/* Nested Trigger: Custom Range */}
        <FlyoutTriggerButton
          flyoutKey="custom"
          label="Custom Range"
          activeFlyout={activeFlyout}
          isGroupSelected={isCustomSelected}
          onFlyoutChange={setActiveFlyout}
        />
      </div>

      {/* Flyout Sub-menus */}
      {activeFlyout === "quarter" && (
        <QuarterFlyoutSubmenu selectedPeriod={selectedPeriod} onSelectPreset={onSelectPreset} />
      )}
      {activeFlyout === "half_year" && (
        <HalfYearFlyoutSubmenu selectedPeriod={selectedPeriod} onSelectPreset={onSelectPreset} />
      )}
      {activeFlyout === "custom" && (
        <CustomRangeFlyoutSubmenu
          pendingRange={pendingRange}
          onPendingRangeChange={setPendingRange}
          onClose={() => setActiveFlyout(null)}
          onApplyCustom={onApplyCustom}
        />
      )}
    </div>
  );
}

/**
 * Ultra-Minimal, Professional Excel-Style GST Filing Modal
 */
function GstFilingModal({ open, onClose, companyCode }) {
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [customRange, setCustomRange] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rawInvoices, setRawInvoices] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState("all");
  const [searchFilter, setSearchFilter] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState(null);
  const [calcBasis, setCalcBasis] = useState("total"); // "total" | "paid"

  const presetOptions = useMemo(() => getPresetOptions(), []);

  const handleSelectPreset = useCallback((presetKey) => {
    setSelectedPeriod(presetKey);
    setSelectedRowKeys(null);
    setDropdownOpen(false);
  }, []);

  const handleApplyCustom = useCallback((range) => {
    if (range?.length === 2) {
      setCustomRange(range);
      setSelectedPeriod("custom");
      setSelectedRowKeys(null);
      setDropdownOpen(false);
    }
  }, []);

  const handleCloseDropdown = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  const renderPeriodDropdown = useCallback(() => (
    <GstPeriodDropdownMenu
      presetOptions={presetOptions}
      selectedPeriod={selectedPeriod}
      customRange={customRange}
      onSelectPreset={handleSelectPreset}
      onClose={handleCloseDropdown}
      onApplyCustom={handleApplyCustom}
    />
  ), [
    presetOptions,
    selectedPeriod,
    customRange,
    handleSelectPreset,
    handleCloseDropdown,
    handleApplyCustom,
  ]);

  const buttonDisplayLabel = useMemo(() => {
    if (selectedPeriod === "custom" && customRange?.[0] && customRange?.[1]) {
      return `${customRange[0].format("DD MMM")} – ${customRange[1].format("DD MMM, YYYY")}`;
    }
    const resolved = resolvePeriodDates(selectedPeriod);
    return resolved.label || "All Dates";
  }, [selectedPeriod, customRange]);

  const fetchPeriodInvoices = useCallback(async () => {
    if (!companyCode) return;
    setLoading(true);

    try {
      const options = { page: 1, pageSize: 500 };
      if (selectedPeriod === "custom" && customRange?.[0] && customRange?.[1]) {
        options.dateFrom = customRange?.[0]?.format("YYYY-MM-DD");
        options.dateTo = customRange?.[1]?.format("YYYY-MM-DD");
      } else if (selectedPeriod !== "all" && selectedPeriod !== "custom") {
        const resolved = resolvePeriodDates(selectedPeriod);
        if (resolved.dateFrom) options.dateFrom = resolved.dateFrom;
        if (resolved.dateTo) options.dateTo = resolved.dateTo;
      }

      const [res, settingsRes] = await Promise.all([
        getInvoices(companyCode, options),
        getCompanySettings(companyCode).catch(() => null),
      ]);

      if (res?.success) {
        setRawInvoices(res.invoices || []);
      } else {
        message.error("Failed to load invoice records.");
      }

      if (settingsRes?.settings) {
        setCompanySettings(settingsRes.settings);
      }
    } catch (err) {
      console.error("Failed to fetch GST invoices:", err);
      message.error("Error loading GST invoice data.");
    } finally {
      setLoading(false);
    }
  }, [companyCode, selectedPeriod, customRange]);

  useEffect(() => {
    if (open) {
      fetchPeriodInvoices();
    }
  }, [open, fetchPeriodInvoices]);

  // Normalized Records
  const normalizedRecords = useMemo(() => {
    return rawInvoices.map((inv) => normalizeGstInvoice(inv, companySettings, calcBasis));
  }, [rawInvoices, companySettings, calcBasis]);

  const selectedRowKeysResolved = useMemo(() => {
    return selectedRowKeys === null
      ? normalizedRecords.map((r) => r._id)
      : selectedRowKeys;
  }, [selectedRowKeys, normalizedRecords]);

  const selectedKeySet = useMemo(() => new Set(selectedRowKeysResolved), [selectedRowKeysResolved]);

  const activeCalculatedRecords = useMemo(() => {
    return normalizedRecords.filter((r) => selectedKeySet.has(r._id));
  }, [normalizedRecords, selectedKeySet]);

  const b2bInvoices = useMemo(() => {
    return normalizedRecords.filter((r) => r.isB2B);
  }, [normalizedRecords]);

  const b2cInvoices = useMemo(() => {
    return normalizedRecords.filter((r) => !r.isB2B);
  }, [normalizedRecords]);

  const rateRows = useMemo(() => {
    return groupInvoicesByGstRate(activeCalculatedRecords);
  }, [activeCalculatedRecords]);

  const hsnRows = useMemo(() => {
    return groupInvoicesByHsn(activeCalculatedRecords);
  }, [activeCalculatedRecords]);

  const displayedHsnRows = useMemo(() => {
    if (!searchFilter.trim()) return hsnRows;
    const q = searchFilter.trim().toLowerCase();
    return hsnRows.filter((r) => r.hsnCode.toLowerCase().includes(q));
  }, [hsnRows, searchFilter]);

  const metrics = useMemo(() => {
    return calculateGstFilingMetrics(activeCalculatedRecords);
  }, [activeCalculatedRecords]);

  // Filtered dataset according to active tab + search text
  const displayedRecords = useMemo(() => {
    let dataset = normalizedRecords;
    if (activeView === "b2b") dataset = b2bInvoices;
    if (activeView === "b2c") dataset = b2cInvoices;

    const query = searchFilter.trim().toLowerCase();
    if (!query) return dataset;

    return dataset.filter((r) => {
      const invNum = (r.invoiceNumber || "").toLowerCase();
      const comp = (r.customerName || "").toLowerCase();
      const gst = (r.customerGst || "").toLowerCase();
      const pos = (r.placeOfSupply || "").toLowerCase();
      const type = (r.invoiceType || (r.isB2B ? "b2b" : "b2c")).toLowerCase();
      const mode = (r.paymentMode || "").toLowerCase();
      const srv = (r.service || "").toLowerCase();
      const hsn = (r.sacHsn || "").toLowerCase();
      return (
        invNum.includes(query) ||
        comp.includes(query) ||
        gst.includes(query) ||
        pos.includes(query) ||
        type.includes(query) ||
        mode.includes(query) ||
        srv.includes(query) ||
        hsn.includes(query)
      );
    });
  }, [normalizedRecords, b2bInvoices, b2cInvoices, activeView, searchFilter]);

  const currentPeriodLabel = useMemo(() => {
    if (selectedPeriod === "custom" && customRange?.[0] != null && customRange?.[1] != null) {
      return `${customRange[0].format("DD-MMM-YYYY")}_to_${customRange[1].format("DD-MMM-YYYY")}`;
    }
    return resolvePeriodDates(selectedPeriod).label;
  }, [selectedPeriod, customRange]);

  const gridColumns = useMemo(() => buildExcelGridColumns(), []);
  const rateColumns = useMemo(() => buildRateGridColumns(), []);
  const hsnColumns = useMemo(() => buildHsnGridColumns(), []);

  const rowSelection = useMemo(() => ({
    selectedRowKeys: selectedRowKeysResolved,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
    columnWidth: 38,
    preserveSelectedRowKeys: true,
  }), [selectedRowKeysResolved]);

  const handleExportCsv = useCallback(() => {
    if (activeCalculatedRecords.length === 0) {
      message.warning("No invoices selected to export.");
      return;
    }
    exportGstr1CsvFile(activeCalculatedRecords, hsnRows, currentPeriodLabel, calcBasis);
    message.success(
      calcBasis === "paid"
        ? "GSTR-1 CSV exported (Paid Amount basis with Table 12 HSN split-up)."
        : "GSTR-1 CSV exported (including HSN split-up)."
    );
  }, [activeCalculatedRecords, hsnRows, currentPeriodLabel, calcBasis]);

  const handleExportInvoicesCsv = useCallback(() => {
    if (activeCalculatedRecords.length === 0) {
      message.warning("No invoices selected to export.");
      return;
    }
    exportInvoicesCsvFile(activeCalculatedRecords, currentPeriodLabel, calcBasis);
    message.success(
      calcBasis === "paid"
        ? "Invoices CSV exported (Paid Amount basis)."
        : "Invoices CSV exported."
    );
  }, [activeCalculatedRecords, currentPeriodLabel, calcBasis]);

  const handleExportHsnCsv = useCallback(() => {
    if (hsnRows.length === 0) {
      message.warning("No HSN records available to export.");
      return;
    }
    exportHsnSummaryCsvFile(hsnRows, currentPeriodLabel);
    message.success("Table 12 HSN Summary CSV exported.");
  }, [hsnRows, currentPeriodLabel]);

  const activeViewCount = useMemo(() => {
    if (activeView === "hsn_summary") return displayedHsnRows.length;
    if (activeView === "rate_summary") return rateRows.length;
    return displayedRecords.length;
  }, [activeView, displayedHsnRows.length, rateRows.length, displayedRecords.length]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      closable={false}
      width={1280}
      centered
      footer={null}
      title={null}
      styles={{
        content: { padding: "16px 20px", borderRadius: 8, overflow: "visible" },
        body: { padding: 0 },
      }}
    >
      <Flex vertical gap={12}>
        {/* Minimal Header & Action Bar on Single Line */}
        <Flex justify="space-between" align="center" wrap={false} gap={10} style={{ width: "100%" }}>
          <Flex align="center" gap={10} style={{ flexShrink: 0 }}>
            <Text strong style={{ fontSize: "1.02rem", color: "#0f172a", letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
              GST Filing
            </Text>

            <Segmented
              value={activeView}
              onChange={(val) => setActiveView(val)}
              options={[
                { label: `All (${normalizedRecords.length})`, value: "all" },
                { label: `B2B (${b2bInvoices.length})`, value: "b2b" },
                { label: `B2C (${b2cInvoices.length})`, value: "b2c" },
                { label: `HSN Summary (${hsnRows.length})`, value: "hsn_summary" },
                { label: "Rate Slabs", value: "rate_summary" },
              ]}
              size="middle"
            />
          </Flex>

          <Flex align="center" gap={6} wrap={false} style={{ flexShrink: 0 }}>
            <Dropdown
              open={dropdownOpen}
              onOpenChange={(nextOpen) => {
                setDropdownOpen(nextOpen);
              }}
              trigger={["click"]}
              placement="bottomLeft"
              autoAdjustOverflow={false}
              menu={{ items: [] }}
              popupRender={renderPeriodDropdown}
              dropdownRender={renderPeriodDropdown}
              getPopupContainer={(trigger) =>
                trigger?.closest?.(".ant-modal-content") || trigger?.parentElement || document.body
              }
              destroyPopupOnHide={false}
            >
              <Button
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  height: 32,
                  padding: "0 10px",
                  borderRadius: 6,
                  borderColor: "#cbd5e1",
                  background: "#ffffff",
                  cursor: "pointer",
                }}
              >
                <CalendarOutlined style={{ color: "#2563eb", fontSize: 13 }} />
                <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500, whiteSpace: "nowrap" }}>
                  {buttonDisplayLabel}
                </span>
                <DownOutlined style={{ fontSize: 10, color: "#64748b" }} />
              </Button>
            </Dropdown>

            <Segmented
              value={calcBasis}
              onChange={(val) => setCalcBasis(val)}
              options={[
                { label: "Total Value", value: "total" },
                { label: "Paid Amount", value: "paid" },
              ]}
              size="middle"
              style={{
                background: "#f1f5f9",
                fontWeight: 500,
                fontSize: "0.78rem",
              }}
            />

            {activeView !== "rate_summary" && (
              <Input
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                placeholder="Filter sheet..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                allowClear
                style={{ width: 135, height: 32, borderRadius: 6 }}
              />
            )}

            <Dropdown
              menu={{
                items: [
                  {
                    key: "full_csv",
                    label: "Full CSV (Invoices + HSN Split)",
                    onClick: handleExportCsv,
                  },
                  {
                    key: "invoices_csv",
                    label: "Invoices Only (.csv)",
                    onClick: handleExportInvoicesCsv,
                  },
                  {
                    key: "hsn_csv",
                    label: "HSN Summary Only (.csv)",
                    onClick: handleExportHsnCsv,
                  },
                ],
              }}
              trigger={["click"]}
            >
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                disabled={loading || normalizedRecords.length === 0}
                style={{
                  height: 32,
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#2563eb",
                  borderColor: "#2563eb",
                }}
              >
                <span>CSV</span>
                <DownOutlined style={{ fontSize: 9, color: "#ffffff" }} />
              </Button>
            </Dropdown>

            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: 13, color: "#64748b" }} />}
              onClick={onClose}
              aria-label="Close"
              style={{
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 6,
                color: "#64748b",
              }}
            />
          </Flex>
        </Flex>

        {/* Minimal Excel Status Strip (Pure Financial Metrics) */}
        <div
          style={{
            padding: "4px 0",
            fontSize: "0.82rem",
            color: "#334155",
          }}
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
            <Flex align="center" gap={12} wrap="wrap">
              <span>
                <Text type="secondary">Invoices: </Text>
                <strong style={{ color: "#0f172a" }}>
                  {metrics.totalInvoices}
                  {normalizedRecords.length !== metrics.totalInvoices
                    ? ` / ${normalizedRecords.length}`
                    : ""}
                </strong>
              </span>
              <Divider type="vertical" style={{ borderColor: "#cbd5e1", margin: 0, height: 14 }} />
              <span>
                <Text type="secondary">Taxable: </Text>
                <strong style={{ fontVariantNumeric: "tabular-nums" }}>₹{formatNumber(metrics.totalTaxable)}</strong>
              </span>
              <Divider type="vertical" style={{ borderColor: "#cbd5e1", margin: 0, height: 14 }} />
              <span>
                <Text type="secondary">CGST: </Text>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>₹{formatNumber(metrics.totalCgst)}</span>
              </span>
              <Divider type="vertical" style={{ borderColor: "#cbd5e1", margin: 0, height: 14 }} />
              <span>
                <Text type="secondary">SGST: </Text>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>₹{formatNumber(metrics.totalSgst)}</span>
              </span>
              <Divider type="vertical" style={{ borderColor: "#cbd5e1", margin: 0, height: 14 }} />
              <span>
                <Text type="secondary">IGST: </Text>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>₹{formatNumber(metrics.totalIgst)}</span>
              </span>
              <Divider type="vertical" style={{ borderColor: "#cbd5e1", margin: 0, height: 14 }} />
              <span>
                <Text type="secondary">Total GST: </Text>
                <strong style={{ color: "#2563eb", fontVariantNumeric: "tabular-nums" }}>
                  ₹{formatNumber(metrics.totalTax)}
                </strong>
              </span>
            </Flex>

            <Flex align="center" gap={10}>
              {calcBasis === "paid" && (
                <>
                  <span style={{ color: "#2563eb", fontWeight: 600, fontSize: "0.78rem" }}>
                    GST on Paid Amount
                  </span>
                  <Divider type="vertical" style={{ borderColor: "#cbd5e1", margin: 0, height: 14 }} />
                </>
              )}
              <div>
                <Text type="secondary">{calcBasis === "paid" ? "Gross Paid: " : "Gross Value: "}</Text>
                <strong style={{ color: "#0f172a", fontSize: "0.92rem", fontVariantNumeric: "tabular-nums" }}>
                  ₹{formatNumber(metrics.totalGross)}
                </strong>
              </div>
            </Flex>
          </Flex>
        </div>

        {/* Excel Spreadsheet Table */}
        {renderSheetTable({
          loading,
          activeView,
          hsnColumns,
          hsnRows: displayedHsnRows,
          rateColumns,
          rateRows,
          gridColumns,
          displayedRecords,
          rowSelection,
          selectedRowKeys: selectedRowKeysResolved,
        })}

        {/* Minimal Footer */}
        <Flex justify="space-between" align="center" style={{ paddingTop: 4 }}>
          <Text type="secondary" style={{ fontSize: "0.75rem" }}>
            Period: {currentPeriodLabel} &bull; {activeCalculatedRecords.length} / {normalizedRecords.length} active ({activeViewCount} shown)
          </Text>
          <Button size="small" onClick={onClose} style={{ borderRadius: 6 }}>
            Close
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}

export default GstFilingModal;
